import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'expo-router';
import { View, Text, TouchableOpacity, useWindowDimensions } from 'react-native';
import ReAnimated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useMemo } from 'react';
import Day from '@/components/Events/Day';
import { Query } from '@/utils/react-query/Queries';
import usePlatform from '@/utils/hooks/usePlatform';
import DropdownMenu from '../General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '../General/Unicon';

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; 
const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const nDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const currentDate = new Date();


/**
 * A component that displays a calendar for a specific month and year.
 * @param {object} props - The properties for the Calendar component.
 * @param {Function} props.onDayPress - A callback function that is called when a day is pressed.
 * @param {boolean} [props.collapsed=false] - Whether the calendar is collapsed or not.
 * @param {Array<string>} [props.markedDates=[]] - An array of dates that are marked in the calendar.
 * @param {Array<string>} [props.triangleMarkedDates=[]] - An array of dates that have a triangle marker in the calendar.
 * @param {Function} [props.onMonthChange=() => {}] - A callback function that is called when the month changes.
 * @param {Function} [props.onYearChange=() => {}] - A callback function that is called when the year changes.
 * @param {boolean} [props.eventsAreLoading=false] - Whether the events are currently loading.
 * @param {number} [props.minYear=null] - The minimum year that can be selected.
 * @param {number} [props.maxYear=null] - The maximum year that can be selected.
 * @returns {JSX.Element} - The rendered calendar component.
 */
const Calendar = ({ onDayPress, initialSelectedDate, collapsed = false, markedDates = [], triangleMarkedDates = [], blackoutDates = {}, blackoutCounts = {}, eventsByDate = {}, onMonthChange = () => {}, onYearChange = () => {}, eventsAreLoading = false, minimumDate = new Date('1900-01-01'), maximumDate, dayBoxHeightOverride = null, canCreateEvents: canCreateEventsProp, disableSelection = false, smallCalendar: smallCalendarProp = false, onEventPress, onAddOccurrence = () => {} }) => {
    
    const [monthShown, setMonthShown] = useState(initialSelectedDate ? initialSelectedDate.getMonth() : currentDate.getMonth());
    const [yearShown, setYearShown] = useState(initialSelectedDate ? initialSelectedDate.getFullYear() : currentDate.getFullYear());
    const [days, setDays] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);

    const { isPortrait, isNative, isTablet } = usePlatform();
    const { height } = useWindowDimensions();
    const router = useRouter();

    // onChange is memoized on month/year, so it must call the latest
    // onDayPress via ref — otherwise presses hit a stale closure from before
    // the parent's data (e.g. blackout periods) finished loading.
    const onDayPressRef = useRef(onDayPress);
    onDayPressRef.current = onDayPress;

    const firstDayOfMonth = new Date(yearShown, monthShown, 1).getDay();
    const smallCalendar = smallCalendarProp || (isPortrait && !isTablet) || (containerWidth > 0 && containerWidth <= 400);

    const OVERALL_PADDING = smallCalendar ? 10 : 0;
    const AVAILABLE_WIDTH = containerWidth > 0 ? containerWidth - (OVERALL_PADDING * 2) - 3 : 350;
    const DAY_BOX_WIDTH = AVAILABLE_WIDTH / 7;
    const DAY_BOX_HEIGHT = dayBoxHeightOverride ?? (smallCalendar ? DAY_BOX_WIDTH : Math.max(80, (height - (isNative ? 140 : 0)) / 7));
    const CALENDAR_HEIGHT = (nDays[monthShown] + firstDayOfMonth > 35 ? DAY_BOX_HEIGHT * 6 : DAY_BOX_HEIGHT * 5) + (smallCalendar ? 80 : 67);


    const handleLayout = useCallback((event) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0 && Math.abs(width - containerWidth) > 1) {
            setContainerWidth(width);
        }
    }, [containerWidth]);

    const {data: loggedInUser} = Query.people.loggedInUser();
    const {data: organization} = Query.organization.byId({ id: loggedInUser?.organizationID, options: { enabled: !!loggedInUser?.organizationID } });
    

    const calendarAnimation = useSharedValue({
        height: CALENDAR_HEIGHT
    }); 

    const heightCalendarAnimationStyle = useAnimatedStyle(() => {
        return {
            height: withTiming(calendarAnimation.value.height, {duration: 300}),
        }
    })

    useEffect(() => {
        calendarAnimation.value = { height: collapsed ? 0 : CALENDAR_HEIGHT }
    }, [collapsed, CALENDAR_HEIGHT])

    useEffect(() => {
        onChange(null)
        onMonthChange(monthShown)
    }, [monthShown])

    useEffect(() => {
        onChange(null)
        onYearChange(yearShown)
    }, [yearShown])


    const addMonth = (monthsToAdd) => {
        if(monthShown + monthsToAdd < 0){
            setYearShown(yearShown - 1);
            setMonthShown(11);
        }
        else if(monthShown + monthsToAdd > 11){
            setYearShown(yearShown + 1);
            setMonthShown(0)
        }
        else{
            setMonthShown(monthShown + monthsToAdd);
        }
    }


    const onChange = useCallback((date) => {
        if(date && !eventsAreLoading){
            onDayPressRef.current?.(date)
        }

        if (date && disableSelection) return;

        setDays(prevItems => {
            if(!prevItems || prevItems.length == 0 || date == null){
                const newDays = [];

                //Spaces before the first day of the month
                for(let j=0; j<firstDayOfMonth; j++){
                    newDays.push({ date: null, isSelected: false })
                }

                //Days of the month
                for(let i=1; i<=nDays[monthShown]; i++){
                    const newDate = new Date(yearShown, monthShown, i, 12);
                    newDays.push({ date: newDate, isSelected: newDate == date || (newDate?.toDateString() == initialSelectedDate?.toDateString() && !date) })
                }

                //Spaces after the last day of the month
                for(let j=0; j<7 - (nDays[monthShown] + firstDayOfMonth) % 7; j++){
                    newDays.push({ date: null, isSelected: false })
                }

                return newDays;
            }
            else{
                return prevItems.map((item) => {
                    return item.date !== date ? { date: item.date, isSelected: false } : { date: date, isSelected: true }
                })
            }
        })
    }, [monthShown, yearShown, eventsAreLoading, disableSelection])


    const newEventOnDay = (date) => {
        const now = new Date();
        date.setHours(now.getHours())
        date.setMinutes(now.getMinutes())

        router.push({pathname:"/events/create", params:{defaultStartTime: date.toISOString()}})
    }


    const yearOptions = useMemo(() => {
        const options = [];
        const thisYear = new Date().getFullYear();

        const minYearValue = minimumDate ? new Date(minimumDate).getFullYear() : 1900;
        const maxYearValue = maximumDate ? new Date(maximumDate).getFullYear() : thisYear;

        for (var i = minYearValue; i <= (maxYearValue); i++) {
            options.unshift(i);
        }

        return options;
    }, [minimumDate, maximumDate]);

    const dayDisplay = days.map((day, i) => {
        const dateStr = day.date?.toDateString();
        const blackoutType = dateStr ? (blackoutDates[dateStr] || (triangleMarkedDates.includes(dateStr) ? 'partial' : 'none')) : 'none';
        return <Day
            smallCalendar={smallCalendar}
            disabled={day.date && ((minimumDate && day.date < minimumDate) || (maximumDate && day.date > maximumDate))}
            key={!day.date ? "empty_" + i : "date_" + day.date}
            blackoutType={blackoutType}
            blackoutCount={dateStr ? (blackoutCounts[dateStr] || 0) : 0}
            date={day.date}
            isSelected={disableSelection ? false : day.isSelected}
            dotColors={!day.date ? [] :markedDates[day.date.toDateString()]}
            events={!day.date ? [] : eventsByDate[day.date.toDateString()] || []}
            size={{width: DAY_BOX_WIDTH, height: DAY_BOX_HEIGHT}}
            onPress={onChange}
            onLongPress={(date) => (canCreateEventsProp !== false && loggedInUser?.can?.('Create Events')) ? newEventOnDay(date) : () => {}}
            onDoubleTap={(date) => (canCreateEventsProp !== false && loggedInUser?.can?.('Create Events')) ? onAddOccurrence(date) : () => {}}
            weekdayIndex={i % 7}
            canCreateEvents={canCreateEventsProp !== undefined ? canCreateEventsProp : loggedInUser?.can?.('Create Events')}
            timezone={organization?.timezone}
            onEventPress={onEventPress}
        />
    })


    const weekDayDisplay = useMemo(() => weekDays.map(day => (
        <View key={"weekdays_" + day} style={styles.weekdayBox(DAY_BOX_WIDTH)}>
            <Text style={styles.weekdayText}>
                {day.toUpperCase()}
            </Text>
        </View>
    )), [DAY_BOX_WIDTH])

    return(
        <ReAnimated.View 
            style={[heightCalendarAnimationStyle, styles.container()]}
            onLayout={handleLayout}
        >
            
            <View style={styles.headerContainer}>
                <TouchableOpacity hitSlop={5} onPress={() => addMonth(-1)} style={styles.navButton}><Unicon name="chevron-back-outline" colorVariant="text" size={24}/></TouchableOpacity>
                <View style={styles.dropdownContainer}>
                    <DropdownMenu
                        title="Select a Month"
                        triggerComponent={<Text style={styles.monthYearText(smallCalendar)}>{months[monthShown]}</Text>}
                        options={months.map(month => ({name: month, disabled: new Date(month + ' 01, ' + yearShown) < minimumDate || new Date(month + ' 31, ' + yearShown) > maximumDate, onSelect: () => setMonthShown(months.indexOf(month))}))}
                    />

                    <DropdownMenu
                        title="Select a Year"
                        triggerComponent={<Text style={styles.monthYearText(smallCalendar)}>{yearShown}</Text>}
                        options={yearOptions.map(year => ({name: year.toString(), onSelect: () => setYearShown(year)}))}
                    />
                </View>
                <TouchableOpacity hitSlop={5} onPress={() => addMonth(1)} style={styles.navButton}><Unicon name="chevron-forward-outline" colorVariant="text" size={24}/></TouchableOpacity>
            </View>

            <View style={styles.bodyContainer(OVERALL_PADDING)}>
                <View style={styles.weekdaysContainer}>
                    {weekDayDisplay}
                </View>

                <View style={styles.daysContainer}>
                    {dayDisplay}
                </View>
            </View>
        </ReAnimated.View>
    )
}


const styles = StyleSheet.create(({colors, fonts, shadows}, rt) => ({
    monthYearText: (smallCalendar) => ({
        color:colors.text,
        fontSize:(smallCalendar ? 14 : 18) * (0.5 + 0.5 * rt.fontScale),
        fontFamily:fonts.heading
    }),
    container: () => ({
        width: '100%',
        backgroundColor:colors.surface, 
        boxShadow:shadows.card, 
        borderRadius:8, 
        overflow:"hidden"
    }),
    headerContainer: {
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center", 
        paddingTop:10
    },
    navButton: {
        paddingHorizontal:10
    },
    dropdownContainer: {
        flexDirection:"row", 
        gap:5
    },
    bodyContainer: (padding) => ({
        padding: padding
    }),
    weekdaysContainer: {
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center", 
        width:"100%"
    },
    daysContainer: {
        paddingVertical:10, 
        flexDirection:"row", 
        alignItems:"center", 
        flexWrap:"wrap", 
        width:"100%"
    },
    weekdayBox: (width) => ({
        width:width, 
        alignItems:"center",
    }),
    weekdayText: {
        color:colors.text,
        fontSize:12 * (0.5 + 0.5 * rt.fontScale),
        fontFamily:fonts.bold,
    }
}))

export default Calendar